// Mensajes de error de acceso en español, que le digan al usuario QUÉ HACER.
// Firebase devuelve textos técnicos en inglés ("Firebase: Error
// (auth/wrong-password).") que no sirven para mostrarle a nadie.
//
// Funciones puras (sin Firebase ni Redux) para poder probarlas con node.

const GOOGLE = "«Continuar con Google»";
const FORGOT = "«Olvidé mi clave»";

// Un Gmail siempre puede entrar con Google, sin crear ni recordar una clave.
export const isGmail = (email) =>
  /@(gmail|googlemail)\.com$/i.test(String(email || "").trim());

// Códigos con los que Firebase rechaza un email + clave. Los dos últimos son
// los que devuelve cuando el proyecto tiene activa la protección contra
// enumeración de emails (no distingue "no existe" de "clave incorrecta").
const CREDENTIAL_CODES = [
  "auth/wrong-password",
  "auth/user-not-found",
  "auth/invalid-credential",
  "auth/invalid-login-credentials",
];

const BY_CODE = {
  "auth/invalid-email":
    "El email no está bien escrito. Revisa que tenga el formato nombre@ejemplo.com.",
  "auth/missing-email": "Escribe tu email.",
  "auth/weak-password": "La clave es muy corta. Usa al menos 6 caracteres.",
  "auth/too-many-requests":
    "Hiciste demasiados intentos seguidos. Espera unos minutos y vuelve a probar.",
  "auth/network-request-failed":
    "No pudimos conectarnos. Revisa tu conexión a internet y vuelve a intentarlo.",
  "auth/user-disabled":
    "Esta cuenta está deshabilitada. Comunícate con el soporte de TicketControl.",
  "auth/account-exists-with-different-credential":
    "Ya existe una cuenta con ese email que entra con clave. Ingresa con tu email y tu clave.",
};

const BY_CONTEXT = {
  login: "No pudimos iniciar sesión. Vuelve a intentarlo en unos minutos.",
  register: "No pudimos crear la cuenta. Vuelve a intentarlo en unos minutos.",
  reset: "No pudimos enviar el email. Vuelve a intentarlo en unos minutos.",
  google: "No pudimos entrar con Google. Vuelve a intentarlo.",
};

export const errorCode = (error) => {
  const message = String(error?.message || error || "");
  // El SDK instalado (9.x) no conoce este código del servidor y lo entrega
  // como auth/internal-error con el nombre crudo dentro del mensaje.
  if (/INVALID_LOGIN_CREDENTIALS/.test(message)) return "auth/invalid-login-credentials";
  if (error?.code) return error.code;
  const match = message.match(/\((auth\/[a-z-]+)\)/);
  return match ? match[1] : "";
};

// Estos errores se explican mejor sabiendo con qué método entra la cuenta
// (fetchSignInMethodsForEmail): una cuenta creada con Google no tiene clave.
export const needsSignInMethods = (error, context) => {
  const code = errorCode(error);
  if (code === "auth/email-already-in-use") return true;
  return context === "login" && CREDENTIAL_CODES.includes(code);
};

const credentialsMessage = (code, email, methods) => {
  const hasGoogle = methods.includes("google.com");
  const hasPassword = methods.includes("password");

  if (hasGoogle && !hasPassword) {
    return `Este email entra con Google, no con clave. Toca ${GOOGLE}.`;
  }
  if (code === "auth/user-not-found") {
    return isGmail(email)
      ? `Todavía no hay una cuenta con este email. Como es de Gmail, entra con ${GOOGLE}: no necesitas registrarte ni crear una clave.`
      : "No encontramos una cuenta con ese email. Revisa que esté bien escrito. Si es tu primera vez, toca «Crea tu cuenta».";
  }
  if (hasPassword) {
    const also = hasGoogle ? ` También puedes entrar con ${GOOGLE}.` : "";
    return `La clave no es correcta. Si no la recuerdas, toca ${FORGOT}.${also}`;
  }
  // No se pudo saber cómo entra la cuenta: decide el dominio del email.
  return isGmail(email)
    ? `No pudimos entrar con esa clave. Como tu email es de Gmail, prueba con ${GOOGLE}: no necesitas clave.`
    : `El email o la clave no son correctos. Revisa que estén bien escritos. Si no recuerdas la clave, toca ${FORGOT}.`;
};

const emailInUseMessage = (email, methods) => {
  const hasPassword = methods.includes("password");
  const usesGoogle = methods.includes("google.com") || (!hasPassword && isGmail(email));
  if (usesGoogle && !hasPassword) {
    return `Ya existe una cuenta con ese email y entra con Google. No necesitas registrarte: toca ${GOOGLE}.`;
  }
  return "Ya existe una cuenta con ese email. No hace falta registrarte de nuevo: vuelve a «Ingresa» y usa tu clave.";
};

/**
 * @param {*} error error de Firebase Auth (o cualquier cosa que se haya lanzado)
 * @param {"login"|"register"|"reset"|"google"} context qué estaba haciendo el usuario
 * @param {{email?: string, methods?: string[]}} extra
 */
export const authErrorMessage = (error, context, { email = "", methods = [] } = {}) => {
  const code = errorCode(error);

  if (context === "login" && CREDENTIAL_CODES.includes(code)) {
    return credentialsMessage(code, email, methods);
  }
  if (code === "auth/email-already-in-use") {
    return emailInUseMessage(email, methods);
  }
  if (context === "reset" && code === "auth/user-not-found") {
    return isGmail(email)
      ? `No hay una cuenta con clave para este email. Como es de Gmail, entra con ${GOOGLE}: no necesitas clave.`
      : "No encontramos una cuenta con ese email. Revisa que esté bien escrito.";
  }
  return (
    BY_CODE[code] ||
    BY_CONTEXT[context] ||
    "Ocurrió un error. Vuelve a intentarlo en unos minutos."
  );
};
